import React, { useEffect, useState } from 'react';
import { Menu, X, Facebook, Twitter, Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { NavLink } from 'react-router-dom';
import { Language } from '../App';
interface NavbarProps {
  language: Language;
  toggleLanguage: () => void;
}
const navItems = [{
  en: 'Home',
  ps: 'کور',
  path: '/'
}, {
  en: 'Magazine',
  ps: 'مجله',
  path: '/magazine'
}, {
  en: 'About',
  ps: 'په اړه',
  path: '/about'
}, {
  en: 'Team',
  ps: 'ټیم',
  path: '/team'
}, {
  en: 'Archive',
  ps: 'آرشیف',
  path: '/archive'
}, {
  en: 'Support',
  ps: 'ملاتړ',
  path: '/subscription'
}, {
  en: 'Contact',
  ps: 'اړیکه',
  path: '/contact'
}];
const logos = {
  english: 'https://cdn.magicpatterns.com/uploads/2WciLPDBTDd6muQs1migkU/English_Logo.jpg',
  pashto: 'https://cdn.magicpatterns.com/uploads/7CKdKs1tCA5hzvjCodsXWM/Pahto_Logo.jpg'
};
export function Navbar({
  language,
  toggleLanguage
}: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isPashto = language === 'pashto';
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);
  const toggleMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  return <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`} dir={isPashto ? 'rtl' : 'ltr'} data-id="element-168">
        {/* Gradient Background with subtle bird pattern */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#C2185B] to-[#7B1FA2] overflow-hidden" data-id="element-169">
          {/* Bird Silhouettes - solid filled birds like reference image */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMaxYMid meet" viewBox="0 0 1200 120" data-id="element-170">
            <g fill="white" opacity="0.12" data-id="element-171">
              {/* Large soaring bird - wings spread wide */}
              <path d="M820,45 C810,30 795,20 780,15 C790,25 795,35 800,42 C790,38 775,32 760,30 C778,38 790,42 800,45 C795,48 790,55 785,65 C795,55 800,48 805,45 C810,48 815,55 820,65 C815,55 812,48 810,45 C820,42 835,38 850,30 C835,32 820,38 810,42 C815,35 820,25 830,15 Z" transform="translate(200,-5) scale(0.9)" data-id="element-172" />

              {/* Medium bird - angled flight */}
              <path d="M920,35 C912,22 900,15 888,12 C896,20 900,28 903,33 C895,30 884,25 872,24 C886,30 895,33 903,35 C900,38 896,44 892,52 C898,44 902,38 905,35 C908,38 912,44 916,52 C912,44 910,38 908,35 C916,33 926,30 938,24 C926,25 916,30 908,33 C912,28 916,20 924,12 Z" transform="translate(50,10) scale(0.7)" data-id="element-173" />

              {/* Small bird top right */}
              <path d="M1020,28 C1014,18 1005,13 996,10 C1002,16 1005,22 1007,26 C1001,24 993,20 984,19 C995,23 1002,26 1007,28 C1005,30 1002,35 999,41 C1003,35 1006,30 1008,28 C1010,30 1013,35 1016,41 C1013,35 1011,30 1010,28 C1015,26 1022,23 1031,19 C1022,20 1015,24 1010,26 C1013,22 1016,16 1022,10 Z" transform="translate(20,5) scale(0.6)" data-id="element-174" />

              {/* Tiny distant birds - simple shapes */}
              <path d="M1080,40 C1076,34 1070,30 1064,28 C1068,32 1070,36 1072,39 L1068,37 C1065,36 1060,35 1056,35 C1062,37 1066,39 1072,40 C1070,42 1068,46 1066,50 C1069,46 1071,42 1073,40 C1075,42 1077,46 1079,50 C1077,46 1076,42 1075,40 C1080,39 1085,37 1090,35 C1085,35 1081,36 1078,37 L1075,39 C1076,36 1078,32 1082,28 Z" transform="translate(10,0) scale(0.5)" data-id="element-175" />

              {/* Bird cluster - right side */}
              <path d="M950,55 C944,45 936,40 928,38 C934,43 936,48 938,53 C932,50 924,47 916,46 C926,49 934,53 938,55 C936,57 934,62 932,68 C935,62 937,57 939,55 C941,57 943,62 946,68 C943,62 942,57 941,55 C947,53 954,49 962,46 C954,47 946,50 941,53 C944,48 946,43 952,38 Z" transform="translate(-30,0) scale(0.55)" data-id="element-176" />

              {/* More scattered small birds */}
              <path d="M860,20 C856,14 850,10 844,8 C848,12 850,16 852,19 C848,17 842,15 836,14 C844,17 850,19 852,20 C850,22 848,26 846,30 C849,26 851,22 853,20 C855,22 857,26 859,30 C857,26 856,22 855,20 C860,19 866,17 872,14 C866,15 860,17 856,19 C858,16 860,12 864,8 Z" transform="scale(0.5)" data-id="element-177" />

              <path d="M780,65 C776,59 770,55 764,53 C768,57 770,61 772,64 C768,62 762,60 756,59 C764,62 770,64 772,65 C770,67 768,71 766,75 C769,71 771,67 773,65 C775,67 777,71 779,75 C777,71 776,67 775,65 C780,64 786,62 792,59 C786,60 780,62 776,64 C778,61 780,57 784,53 Z" transform="scale(0.45)" data-id="element-178" />

              {/* Extra tiny birds for depth */}
              <path d="M1100,55 L1095,50 L1100,52 L1105,50 Z" transform="scale(0.8)" data-id="element-179" />
              <path d="M1130,35 L1126,31 L1130,33 L1134,31 Z" transform="scale(0.7)" data-id="element-180" />
              <path d="M1060,65 L1056,61 L1060,63 L1064,61 Z" transform="scale(0.6)" data-id="element-181" />
              <path d="M700,50 L697,47 L700,48.5 L703,47 Z" transform="scale(0.7)" data-id="element-182" />
              <path d="M730,35 L727,32 L730,33.5 L733,32 Z" transform="scale(0.6)" data-id="element-183" />
            </g>
          </svg>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-184">
          <div className={`flex items-center justify-between transition-all duration-300 ${isScrolled ? 'h-20 md:h-24' : 'h-24 md:h-28'}`} data-id="element-185">
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center" data-id="element-186">
              <img src={isPashto ? logos.pashto : logos.english} alt="Pasoon Logo" className={`rounded-full object-cover border-2 border-white/30 shadow-lg transition-all duration-300 ${isScrolled ? 'h-14 w-14 md:h-18 md:w-18' : 'h-16 w-16 md:h-20 md:w-20'}`} data-id="element-187" />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8" data-id="element-188">
              <ul className="flex items-center gap-6" data-id="element-189">
                {navItems.map((item, idx) => <li key={idx} data-id="element-190">
                    <NavLink to={item.path} className={({
                  isActive
                }) => `text-white/90 hover:text-white font-medium text-sm tracking-wide transition-colors relative group ${isPashto ? 'font-pashto text-lg' : 'font-sans'} ${isActive ? 'text-white' : ''}`} data-id="element-191">
                      {({
                    isActive
                  }) => <>
                          {isPashto ? item.ps : item.en}
                          <span className={`absolute -bottom-1 left-0 h-0.5 bg-white transition-all duration-300 ${isActive ? 'w-full' : 'w-0 group-hover:w-full'}`} data-id="element-192"></span>
                        </>}
                    </NavLink>
                  </li>)}
              </ul>

              {/* Language Toggle (Desktop) */}
              <button onClick={toggleLanguage} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 transition-all text-white text-xs font-bold tracking-wider backdrop-blur-sm" aria-label="Toggle Language" data-id="element-193">
                <Globe className="w-3.5 h-3.5 opacity-80" data-id="element-194" />
                <span className={!isPashto ? 'opacity-100' : 'opacity-50'} data-id="element-195">
                  EN
                </span>
                <span className="opacity-40" data-id="element-196">|</span>
                <span className={isPashto ? 'opacity-100' : 'opacity-50'} data-id="element-197">
                  PS
                </span>
              </button>
            </nav>

            {/* Mobile/Tablet Actions */}
            <div className="flex lg:hidden items-center gap-4" data-id="element-198">
              {/* Language Toggle (Mobile) */}
              <button onClick={toggleLanguage} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 active:bg-white/20 border border-white/20 transition-all text-white text-xs font-bold tracking-wider backdrop-blur-sm" data-id="element-199">
                <span className={!isPashto ? 'opacity-100' : 'opacity-50'} data-id="element-200">
                  EN
                </span>
                <span className="opacity-40" data-id="element-201">|</span>
                <span className={isPashto ? 'opacity-100' : 'opacity-50'} data-id="element-202">
                  PS
                </span>
              </button>

              {/* Hamburger */}
              <button onClick={toggleMenu} className="p-2 text-white hover:bg-white/10 rounded-full transition-colors" aria-label="Open Menu" data-id="element-203">
                <Menu className="w-6 h-6" data-id="element-204" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Full-Screen Menu Overlay */}
      <AnimatePresence data-id="element-205">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="fixed inset-0 z-[60] bg-gradient-to-br from-[#C2185B] to-[#7B1FA2] flex flex-col" dir={isPashto ? 'rtl' : 'ltr'} data-id="element-206">
            {/* Overlay Header */}
            <div className="flex items-center justify-between px-4 sm:px-6 h-20 md:h-24" data-id="element-207">
              <img src={isPashto ? logos.pashto : logos.english} alt="Pasoon Logo" className="h-12 w-12 rounded-full border-2 border-white/20" data-id="element-208" />
              <button onClick={toggleMenu} className="p-2 text-white hover:bg-white/10 rounded-full transition-colors" aria-label="Close Menu" data-id="element-209">
                <X className="w-8 h-8" data-id="element-210" />
              </button>
            </div>

            {/* Overlay Nav Items */}
            <div className="flex-1 flex flex-col justify-center px-8 sm:px-12" data-id="element-211">
              <nav className="flex flex-col gap-6 sm:gap-8" data-id="element-212">
                {navItems.map((item, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              x: isPashto ? 20 : -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.1 + idx * 0.05
            }} data-id="element-213">
                    <NavLink to={item.path} onClick={toggleMenu} className={({
                isActive
              }) => `text-white text-3xl sm:text-4xl font-bold tracking-wide hover:text-white/80 transition-colors ${isPashto ? 'font-pashto' : 'font-sans'} ${isActive ? 'opacity-100' : 'opacity-70'}`} data-id="element-214">
                      {isPashto ? item.ps : item.en}
                    </NavLink>
                  </motion.div>)}
              </nav>
            </div>

            {/* Overlay Footer (Socials) */}
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="p-8 sm:p-12 flex items-center gap-6" data-id="element-215">
              <a href="#" className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors" data-id="element-216">
                <Facebook className="w-5 h-5" data-id="element-217" />
              </a>
              <a href="#" className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors" data-id="element-218">
                <Twitter className="w-5 h-5" data-id="element-219" />
              </a>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}